<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catRed" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-alert-circle" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="catRed"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ allergies.length }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar alergias</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhuma alergia registrada ainda.
        </p>
        <v-btn small color="catRed" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Adicionar alergia
        </v-btn>
      </div>

      <!-- Lista de alergias -->
      <div v-else class="allergies-list">
        <v-chip
          v-for="allergy in allergies"
          :key="allergy.id"
          class="allergy-chip"
          color="#FEF2F2"
          text-color="catRed"
          label
        >
          <v-icon left small>mdi-alert-circle</v-icon>
          <span class="chip-text">{{ allergy.allergen }}</span>
          <v-chip
            v-if="allergy.severity"
            x-small
            :color="severityColor(allergy.severity)"
            text-color="white"
            class="ml-2 flex-shrink-0"
          >
            {{ severityLabel(allergy.severity) }}
          </v-chip>
        </v-chip>
      </div>
    </v-card-text>

    <EditAllergiesDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :dialog-title="`Alergias do paciente`"
      :allergies="allergies"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getAllergiesByPatient } from "@/services/allergyService";
import EditAllergiesDialog from "@/features/patients/profile-dashboard/dialogs/EditAllergiesDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "AllergiesCard",

  components: {
    EditAllergiesDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      dialogVisible: false,
      allergies: [],
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.allergies || []).length;
    },
    hasData() {
      return this.allergies.length > 0;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.allergies = await dashboardStore.section(this.patientId, "allergies", () => getAllergiesByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar alergias:", error);
        this.allergies = [];
      }
    },

    severityLabel(severity) {
      return (
        { mild: "Leve", moderate: "Moderada", severe: "Grave" }[severity] || ""
      );
    },

    severityColor(severity) {
      return (
        { mild: "amber darken-2", moderate: "orange darken-2", severe: "red darken-2" }[
          severity
        ] || "grey"
      );
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Vermelho (Alergias) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #fecaca !important;
  box-shadow: 0 2px 10px rgba(185, 28, 28, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #fca5a5 !important;
  box-shadow: 0 6px 20px rgba(185, 28, 28, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: var(--ds-soft-red);
  border-bottom: 1px solid #fecaca;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-red);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #fca5a5;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-cat-red);
}

.allergies-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.allergy-chip {
  font-size: 12px !important;
  font-weight: 600 !important;
  height: auto !important;
  min-height: 38px !important;
  padding: 8px 12px !important;
  white-space: normal !important;
  overflow-wrap: break-word;
  word-break: normal;
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 10px !important;
}

.chip-text {
  flex: 1;
  text-align: left;
  line-height: 1.4;
}
</style>
